import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/api';
import { Plus, Search, Loader2, Building2, Tag, Trash2, Landmark } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { HealthScoreBadge } from '@/components/FleetHealthScore';

const STATE_COLORS = {
  NEW_LEAD: 'text-[var(--text-muted)] bg-[var(--surface)] border-[var(--border)]',
  SALES_REQ_APP: 'text-blue-400 bg-blue-900/20 border-blue-800/50',
  APPLICATION_SENT: 'text-orange-400 bg-orange-900/20 border-orange-800/50',
  ACCOUNTING_MORE_INFO: 'text-amber-400 bg-amber-900/20 border-amber-800/50',
  ACCOUNTING_APPROVED: 'text-teal-400 bg-teal-900/20 border-teal-800/50',
  DECLINED: 'text-[var(--error)] bg-[var(--error-muted)] border-[var(--error)]',
  CONTRACT_SENT: 'text-cyan-400 bg-cyan-900/20 border-cyan-800/50',
  CONTRACT_SIGNED: 'text-[#57EB31] bg-[#57EB31]/10 border-[#57EB31]/40',
};

const STATE_LABELS = {
  NEW_LEAD: 'NEW LEAD',
  SALES_REQ_APP: 'SALES REQ APP',
  APPLICATION_SENT: 'APPLICATION SENT',
  ACCOUNTING_MORE_INFO: 'ACCOUNTING MORE INFO',
  ACCOUNTING_APPROVED: 'ACCOUNTING APPROVED',
  DECLINED: 'DECLINED',
  CONTRACT_SENT: 'CONTRACT SENT',
  CONTRACT_SIGNED: 'CONTRACT SIGNED',
};

const TENANT_BADGES = {
  MM: { label: 'MM', color: 'text-emerald-400 bg-emerald-900/20 border-emerald-700/50' },
  MT: { label: 'MT', color: 'text-violet-400 bg-violet-900/20 border-violet-700/50' },
};

export default function Customers() {
  const { user } = useAuth();
  const canCreateDirect = ['master_admin', 'admin'].includes(user?.role);
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ company_name: '', contact_email: '', contact_phone: '', address: '', fleet_size: '', notes: '', tenant_group: 'MM', pricing_tier_id: '', billing_mode: '', billing_rate: '', billing_units_limit: '' });
  const [saving, setSaving] = useState(false);
  const [healthScores, setHealthScores] = useState({});
  const [scoresLoading, setScoresLoading] = useState(false);
  const [billingStatuses, setBillingStatuses] = useState({});
  const [pricingTiers, setPricingTiers] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { load(); loadPricingTiers(); }, [search, tenantFilter]);

  const loadPricingTiers = async () => {
    try {
      const res = await api.get('/pricing-tiers');
      setPricingTiers(res.data.tiers || []);
      // Set default tier (Tier 2) if available
      const tier2 = res.data.tiers?.find(t => t.name === 'Tier 2');
      if (tier2 && !form.pricing_tier_id) {
        setForm(prev => ({ ...prev, pricing_tier_id: tier2.id }));
      }
    } catch (e) {
      console.error('Failed to load pricing tiers');
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      // Only show fully-signed customers here. Pipeline (pre-signed) leads live on /ops/pipeline
      const params = { search: search || undefined, limit: 50, state: 'CONTRACT_SIGNED,ONBOARDED' };
      if (tenantFilter) params.tenant_group = tenantFilter;
      const res = await api.get('/customers', { params });
      setCustomers(res.data.items);
      setTotal(res.data.total);
      // All visible customers are signed → load health scores for all
      const active = res.data.items;
      if (active.length > 0) {
        setScoresLoading(true);
        const scoreMap = {};
        await Promise.all(active.map(async (c) => {
          try {
            const r = await api.get(`/health-scores/${c.id}`);
            scoreMap[c.id] = r.data;
          } catch {}
        }));
        setHealthScores(scoreMap);
        setScoresLoading(false);
      }
      // Load billing statuses (single bulk call)
      try {
        const bs = await api.get('/billing/statuses');
        setBillingStatuses(bs.data.statuses || {});
      } catch {
        // silent — non-billing roles will 403
      }
    } catch (e) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/customers/${deleteTarget.id}`);
      toast.success(`${deleteTarget.name} deleted`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        fleet_size: form.fleet_size ? Number(form.fleet_size) : undefined,
        billing_mode: form.billing_mode || null,
        billing_rate: form.billing_mode && form.billing_rate !== '' ? Number(form.billing_rate) : null,
        billing_units_limit: form.billing_mode === 'PER_UNIT' && form.billing_units_limit !== '' && form.billing_units_limit !== null && form.billing_units_limit !== undefined ? Number(form.billing_units_limit) : null,
      };
      await api.post('/customers', payload);
      toast.success('Customer created!');
      setShowCreate(false);
      setForm({ company_name: '', contact_email: '', contact_phone: '', address: '', fleet_size: '', notes: '', tenant_group: 'MM', pricing_tier_id: '', billing_mode: '', billing_rate: '', billing_units_limit: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create customer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 animate-fade-in" data-testid="customers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-['Manrope'] font-bold text-2xl" style={{ color: 'var(--text-primary)' }}>Customers</h1>
          <p className="font-['IBM_Plex_Sans'] text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            {total} signed {total === 1 ? 'customer' : 'customers'} · <a href="/ops/pipeline" className="underline hover:opacity-80" style={{ color: 'var(--primary)' }}>view pipeline →</a>
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="create-customer-btn"
          disabled={!canCreateDirect}
          title={canCreateDirect
            ? 'Add a customer directly (admin override — bypasses lead pipeline)'
            : 'Direct customer creation is restricted to admins. Add a lead via Sales → My Leads instead.'}
          className="btn-primary flex items-center gap-2 text-sm uppercase tracking-wide disabled:opacity-40 disabled:cursor-not-allowed">
          <Plus size={14} /> New Customer
        </button>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-faint)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            className="w-full input-slate pl-9"
            placeholder="Search by company name..." data-testid="customer-search-input" />
        </div>
        <div>
          <select value={tenantFilter} onChange={e => setTenantFilter(e.target.value)} 
            className="select-slate min-w-[120px]" data-testid="tenant-filter">
            <option value="">All Tenants</option>
            <option value="MM">MM Only</option>
            <option value="MT">MT Only</option>
          </select>
        </div>
      </div>

      <div className="card-slate overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--primary)' }} /></div>
        ) : (
          <table className="w-full table-comfortable" data-testid="customers-table">
            <thead>
              <tr>
                {[
                  { label: 'Tenant', cls: '' },
                  { label: 'Company', cls: '' },
                  { label: 'Contact', cls: '' },
                  { label: 'Tier', cls: 'hidden md:table-cell' },
                  { label: 'Billing', cls: 'hidden xl:table-cell' },
                  { label: 'State', cls: '' },
                  { label: 'Fleet', cls: 'hidden lg:table-cell num' },
                  { label: 'Health', cls: '' },
                  { label: 'Created', cls: 'hidden lg:table-cell' },
                  { label: '', cls: '' },
                ].map((h, i) => (
                  <th key={i} className={h.cls}>{h.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {customers.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-16">
                  <Building2 size={32} className="mx-auto mb-3" style={{ color: 'var(--border)' }} />
                  <p className="font-['IBM_Plex_Sans'] text-sm" style={{ color: 'var(--text-muted)' }}>No customers yet</p>
                </td></tr>
              ) : customers.map((c) => {
                const tenantBadge = TENANT_BADGES[c.tenant_group] || { label: c.tenant_group || 'N/A', color: 'text-gray-400 bg-gray-900/20 border-gray-700/50' };
                const tierInfo = pricingTiers.find(t => t.id === c.pricing_tier_id);
                return (
                <tr key={c.id} onClick={() => navigate(`/ops/customers/${c.id}`)} data-testid={`customer-row-${c.id}`}
                  className="cursor-pointer">
                  <td>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border font-['JetBrains_Mono'] ${tenantBadge.color}`}
                      data-testid={`tenant-${c.id}`}>
                      <Tag size={10} />
                      {tenantBadge.label}
                    </span>
                  </td>
                  <td className="font-medium" style={{ color: 'var(--text-primary)' }}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span>{c.company_name}</span>
                      {(() => {
                        const bs = billingStatuses[c.id];
                        if (bs === 'COMPLETE') return null;
                        const isApproved = ['ACCOUNTING_APPROVED','CONTRACT_SENT','CONTRACT_SIGNED'].includes(c.onboarding_state);
                        if (!isApproved) return null;
                        if (bs === 'LINK_SENT') return (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold border font-['JetBrains_Mono'] text-amber-300 bg-amber-900/20 border-amber-700/50"
                            data-testid={`billing-link-sent-${c.id}`} title="Billing setup link sent — awaiting customer">
                            <Landmark size={8} /> LINK SENT
                          </span>
                        );
                        return (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold border font-['JetBrains_Mono'] text-orange-300 bg-orange-900/20 border-orange-700/50"
                            data-testid={`billing-pending-${c.id}`} title="No billing info on file">
                            <Landmark size={8} /> BILLING PENDING
                          </span>
                        );
                      })()}
                    </div>
                  </td>
                  <td style={{ color: 'var(--text-muted)' }}>{c.contact_email}</td>
                  <td className="hidden md:table-cell">
                    {tierInfo ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border font-['JetBrains_Mono'] text-amber-400 bg-amber-900/20 border-amber-700/50"
                        data-testid={`tier-${c.id}`}>
                        {tierInfo.name} ({tierInfo.markup_percent}%)
                      </span>
                    ) : (
                      <span className="text-[10px] font-['JetBrains_Mono']" style={{ color: 'var(--text-faint)' }}>—</span>
                    )}
                  </td>
                  <td className="hidden xl:table-cell">
                    {c.billing_mode === 'MONTHLY' ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border font-['JetBrains_Mono'] text-blue-300 bg-blue-900/20 border-blue-700/50" title="Flat monthly fee">
                        ${Number(c.billing_rate || 0).toFixed(0)}/mo
                      </span>
                    ) : c.billing_mode === 'PER_UNIT' ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border font-['JetBrains_Mono'] text-emerald-300 bg-emerald-900/20 border-emerald-700/50"
                        title={c.billing_units_limit ? `$${Number(c.billing_rate).toFixed(2)} per truck × cap of ${c.billing_units_limit}` : `$${Number(c.billing_rate).toFixed(2)} per truck (no cap)`}>
                        ${Number(c.billing_rate || 0).toFixed(2)}/unit{c.billing_units_limit ? ` (max ${c.billing_units_limit})` : ''}
                      </span>
                    ) : (
                      <span className="text-[10px] font-['JetBrains_Mono']" style={{ color: 'var(--text-faint)' }}>markup only</span>
                    )}
                  </td>
                  <td>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border font-['IBM_Plex_Sans'] ${STATE_COLORS[c.onboarding_state] || STATE_COLORS.NEW_LEAD}`}>
                      {STATE_LABELS[c.onboarding_state] || c.onboarding_state?.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="font-['JetBrains_Mono'] hidden lg:table-cell num" style={{ color: 'var(--text-muted)' }}>{c.fleet_size || '—'}</td>
                  <td>
                    {['ACCOUNTING_APPROVED','CONTRACT_SENT','CONTRACT_SIGNED'].includes(c.onboarding_state) ? (
                      <HealthScoreBadge
                        score={healthScores[c.id]?.score}
                        label={healthScores[c.id]?.label}
                        color={healthScores[c.id]?.color}
                        loading={scoresLoading && !healthScores[c.id]}
                      />
                    ) : <span className="text-xs font-['JetBrains_Mono']" style={{ color: 'var(--border)' }}>—</span>}
                  </td>
                  <td className="text-xs font-['IBM_Plex_Sans'] hidden lg:table-cell" style={{ color: 'var(--text-faint)' }}>
                    {c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => setDeleteTarget({ id: c.id, name: c.company_name })}
                      data-testid={`delete-customer-${c.id}`}
                      title="Delete customer"
                      className="p-1.5 rounded transition-colors"
                      style={{ color: 'var(--text-faint)' }}
                      onMouseEnter={e => { e.currentTarget.style.color = 'var(--error)'; e.currentTarget.style.background = 'var(--error-muted)'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-faint)'; e.currentTarget.style.background = 'transparent'; }}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--text-primary)' }} className="font-['Manrope']">New Customer</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            {/* Tenant Selection */}
            <div>
              <label className="block text-[10px] uppercase tracking-widest font-['IBM_Plex_Sans'] mb-1.5" style={{ color: 'var(--warning)' }}>Tenant *</label>
              <div className="flex gap-3">
                <label className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-md cursor-pointer border-2 transition-colors ${form.tenant_group === 'MM' ? 'border-emerald-500 bg-emerald-900/20' : 'border-[var(--border)]'}`}>
                  <input type="radio" name="tenant" value="MM" checked={form.tenant_group === 'MM'} 
                    onChange={e => setForm(p => ({ ...p, tenant_group: e.target.value }))}
                    className="sr-only" />
                  <Tag size={14} style={{ color: 'rgb(16, 185, 129)' }} />
                  <span className="font-['IBM_Plex_Sans'] font-bold text-sm" style={{ color: form.tenant_group === 'MM' ? 'rgb(16, 185, 129)' : 'var(--text-muted)' }}>MM</span>
                </label>
                <label className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-md cursor-pointer border-2 transition-colors ${form.tenant_group === 'MT' ? 'border-violet-500 bg-violet-900/20' : 'border-[var(--border)]'}`}>
                  <input type="radio" name="tenant" value="MT" checked={form.tenant_group === 'MT'} 
                    onChange={e => setForm(p => ({ ...p, tenant_group: e.target.value }))}
                    className="sr-only" />
                  <Tag size={14} style={{ color: 'rgb(139, 92, 246)' }} />
                  <span className="font-['IBM_Plex_Sans'] font-bold text-sm" style={{ color: form.tenant_group === 'MT' ? 'rgb(139, 92, 246)' : 'var(--text-muted)' }}>MT</span>
                </label>
              </div>
            </div>

            {/* Pricing Tier Selection - REQUIRED */}
            <div>
              <label className="block text-[10px] uppercase tracking-widest font-['IBM_Plex_Sans'] mb-1.5" style={{ color: 'var(--primary)' }}>Pricing Tier * (REQUIRED)</label>
              <select 
                value={form.pricing_tier_id} 
                onChange={e => setForm(p => ({ ...p, pricing_tier_id: e.target.value }))}
                required
                className="w-full select-slate"
                data-testid="pricing-tier-select"
              >
                <option value="">Select pricing tier...</option>
                {pricingTiers.map(tier => (
                  <option key={tier.id} value={tier.id}>
                    {tier.name} — {tier.markup_percent}% markup
                  </option>
                ))}
              </select>
              {!form.pricing_tier_id && (
                <p className="text-xs mt-1" style={{ color: 'var(--error)' }}>
                  Every customer must have a pricing tier assigned
                </p>
              )}
            </div>

            {/* Recurring Billing — Optional */}
            <div>
              <label className="block text-[10px] uppercase tracking-widest font-['IBM_Plex_Sans'] mb-1.5" style={{ color: 'var(--text-muted)' }}>
                Recurring Billing (Optional — in addition to tier markup)
              </label>
              <div className="grid grid-cols-3 gap-2 mb-2">
                <label className={`flex items-center justify-center gap-1.5 p-2 rounded-md cursor-pointer border-2 transition-colors text-xs ${form.billing_mode === '' ? 'border-slate-500 bg-slate-800/40' : 'border-[var(--border)]'}`}>
                  <input type="radio" name="billing_mode" value="" checked={form.billing_mode === ''}
                    onChange={() => setForm(p => ({ ...p, billing_mode: '', billing_rate: '' }))}
                    className="sr-only" data-testid="billing-mode-none" />
                  <span className="font-['IBM_Plex_Sans'] font-bold" style={{ color: form.billing_mode === '' ? 'var(--text-primary)' : 'var(--text-muted)' }}>None</span>
                </label>
                <label className={`flex items-center justify-center gap-1.5 p-2 rounded-md cursor-pointer border-2 transition-colors text-xs ${form.billing_mode === 'MONTHLY' ? 'border-blue-500 bg-blue-900/20' : 'border-[var(--border)]'}`}>
                  <input type="radio" name="billing_mode" value="MONTHLY" checked={form.billing_mode === 'MONTHLY'}
                    onChange={() => setForm(p => ({ ...p, billing_mode: 'MONTHLY' }))}
                    className="sr-only" data-testid="billing-mode-monthly" />
                  <span className="font-['IBM_Plex_Sans'] font-bold" style={{ color: form.billing_mode === 'MONTHLY' ? 'rgb(96, 165, 250)' : 'var(--text-muted)' }}>Monthly Flat</span>
                </label>
                <label className={`flex items-center justify-center gap-1.5 p-2 rounded-md cursor-pointer border-2 transition-colors text-xs ${form.billing_mode === 'PER_UNIT' ? 'border-emerald-500 bg-emerald-900/20' : 'border-[var(--border)]'}`}>
                  <input type="radio" name="billing_mode" value="PER_UNIT" checked={form.billing_mode === 'PER_UNIT'}
                    onChange={() => setForm(p => ({ ...p, billing_mode: 'PER_UNIT' }))}
                    className="sr-only" data-testid="billing-mode-perunit" />
                  <span className="font-['IBM_Plex_Sans'] font-bold" style={{ color: form.billing_mode === 'PER_UNIT' ? 'rgb(16, 185, 129)' : 'var(--text-muted)' }}>Per Truck</span>
                </label>
              </div>
              {form.billing_mode && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-['JetBrains_Mono']" style={{ color: 'var(--text-muted)' }}>$</span>
                  <input type="number" min="0" step="0.01" value={form.billing_rate}
                    onChange={e => setForm(p => ({ ...p, billing_rate: e.target.value }))}
                    placeholder={form.billing_mode === 'MONTHLY' ? '99.00' : '5.00'}
                    required
                    className="flex-1 input-slate" data-testid="billing-rate-input" />
                  <span className="text-xs font-['IBM_Plex_Sans']" style={{ color: 'var(--text-faint)' }}>
                    {form.billing_mode === 'MONTHLY' ? 'per month (flat)' : 'per truck per month'}
                  </span>
                </div>
              )}
              {form.billing_mode === 'PER_UNIT' && (
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>Max trucks allowed:</span>
                  <input type="number" min="0" step="1" value={form.billing_units_limit || ''}
                    onChange={e => setForm(p => ({ ...p, billing_units_limit: e.target.value }))}
                    placeholder="(no cap)"
                    className="w-32 input-slate" data-testid="billing-units-limit-input" />
                  <span className="text-xs font-['IBM_Plex_Sans']" style={{ color: 'var(--text-faint)' }}>
                    Blocks new trucks once reached. Leave empty for no cap.
                  </span>
                </div>
              )}
              {form.billing_mode === 'PER_UNIT' && form.billing_rate && form.fleet_size && (
                <p className="text-xs mt-1.5 font-['JetBrains_Mono']" style={{ color: 'var(--text-faint)' }}>
                  Estimated: ${(Number(form.billing_rate) * Number(form.fleet_size)).toFixed(2)}/month ({form.fleet_size} trucks × ${Number(form.billing_rate).toFixed(2)})
                </p>
              )}
            </div>
            
            {[
              { label: 'Company Name *', key: 'company_name', type: 'text', required: true },
              { label: 'Contact Email *', key: 'contact_email', type: 'email', required: true },
              { label: 'Contact Phone', key: 'contact_phone', type: 'tel', required: false },
              { label: 'Address', key: 'address', type: 'text', required: false },
              { label: 'Fleet Size', key: 'fleet_size', type: 'number', required: false },
            ].map(({ label, key, type, required }) => (
              <div key={key}>
                <label className="block text-[10px] uppercase tracking-widest font-['IBM_Plex_Sans'] mb-1.5" style={{ color: 'var(--text-muted)' }}>{label}</label>
                <input type={type} required={required} value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                  className="w-full input-slate" />
              </div>
            ))}
            <div>
              <label className="block text-[10px] uppercase tracking-widest font-['IBM_Plex_Sans'] mb-1.5" style={{ color: 'var(--text-muted)' }}>Notes</label>
              <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2}
                className="w-full input-slate resize-none" />
            </div>
            <button type="submit" disabled={saving || !form.pricing_tier_id} data-testid="create-customer-submit"
              className="w-full btn-primary flex items-center justify-center gap-2 text-sm uppercase tracking-wide disabled:opacity-50 disabled:cursor-not-allowed">
              {saving && <Loader2 size={14} className="animate-spin" />}
              {saving ? 'Creating...' : 'Create Customer'}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--error)' }} className="font-['Manrope']">Delete Customer</DialogTitle>
          </DialogHeader>
          <p className="text-sm font-['IBM_Plex_Sans'] mt-2" style={{ color: 'var(--text-muted)' }}>
            Are you sure you want to delete <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{deleteTarget?.name}</span>? This cannot be undone.
          </p>
          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting} style={{ background: 'var(--error)', color: 'white' }} data-testid="confirm-delete-customer">
              {deleting ? <Loader2 size={14} className="animate-spin mr-2" /> : <Trash2 size={14} className="mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
